import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plane, ShieldCheck, Dumbbell, Waves, Mountain, Utensils, Star, ArrowRight, Check, MapPin, Calendar, Users,
} from "lucide-react";
import api from "../lib/api";
import { fmtDate } from "../lib/date";
import Reveal from "../components/Reveal";
import { PillButton, Overline } from "../components/primitives";

export default function Home() {
  const [retreat, setRetreat] = useState(null);
  const [founders, setFounders] = useState([]);
  const [content, setContent] = useState({});

  useEffect(() => {
    api.get("/retreats").then((r) => setRetreat(r.data[0])).catch(() => {});
    api.get("/founders").then((r) => setFounders(r.data)).catch(() => {});
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const home = content.home || {};
  const testimonials = content.testimonials || [];
  const price = retreat?.price;

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={home.hero_image || "https://images.unsplash.com/photo-1740712686694-24eb2ad2a16d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"}
            alt="El Salvador coast"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28 pt-32 w-full text-white">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary/90 px-4 py-1.5 text-xs font-semibold tracking-wide">
                <Plane className="w-3.5 h-3.5" /> Flights Included
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-xs font-semibold tracking-wide">
                Adults 18+ Only
              </span>
              <span className="overline text-white/80">{home.hero_overline || "El Salvador"}</span>
            </div>
            <h1 className="font-display font-extrabold tracking-tighter leading-[0.92] text-5xl md:text-7xl lg:text-8xl max-w-4xl">
              {home.hero_title || "Train Hard. Travel Far. Live Fit."}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
              {home.hero_subtitle || "An all-inclusive Wellness Retreat on the Pacific coast of El Salvador."}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/book"><PillButton data-testid="hero-book-btn" className="h-14 px-9 text-base">Book Your Retreat <ArrowRight className="w-4 h-4" /></PillButton></Link>
              <Link to="/retreat"><PillButton variant="light" data-testid="hero-explore-btn" className="h-14 px-9 text-base">Explore the Retreat</PillButton></Link>
            </div>
            {retreat && (
              <div className="mt-12 flex flex-wrap gap-8 text-sm">
                <div><div className="overline text-white/60">Next Departure</div><div className="mt-1 font-display text-lg font-bold">{fmtDate(retreat.start_date)}</div></div>
                <div><div className="overline text-white/60">Starting From</div><div className="mt-1 font-display text-lg font-bold">${price?.toLocaleString()}</div></div>
                <div><div className="overline text-white/60">Spots Remaining</div><div className="mt-1 font-display text-lg font-bold">{retreat.spots_remaining} of {retreat.capacity}</div></div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* HIGHLIGHTS STRIP */}
      <section className="bg-[#0D1110] text-white py-16 relative">
        <div className="noise-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: Dumbbell, label: "Hybrid Fitness", sub: "Daily coached sessions" },
            { icon: Waves, label: "World-Class Surf", sub: "Pacific breaks" },
            { icon: Mountain, label: "Volcano Adventure", sub: "Santa Ana hike" },
            { icon: Utensils, label: "All Meals", sub: "Healthy & delicious" },
          ].map((h, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="flex flex-col gap-3">
                <h.icon className="w-8 h-8 text-primary" strokeWidth={1.75} />
                <div className="font-display font-bold text-lg">{h.label}</div>
                <div className="text-white/50 text-sm">{h.sub}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16 items-center">
          <Reveal>
            <Overline>The Experience</Overline>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-4xl md:text-5xl leading-[1.05]">
              A premium all-inclusive Wellness Retreat.
            </h2>
            <p className="mt-6 text-lg text-foreground/70 leading-relaxed">
              Six days of Hybrid Fitness, Pilates, adventure, and culture — fully hosted by founders Aida and Edwin Colindres.
              Flights, hotel, transport, meals, and every training session are handled. You just show up ready to move.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {(retreat?.highlights || []).slice(0, 4).map((h, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                  <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {h}
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 gap-4">
              <img src="https://images.unsplash.com/photo-1616028872586-f629c1f29968?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Surf" className="rounded-2xl h-64 w-full object-cover mt-8" />
              <img src="https://images.unsplash.com/photo-1691349810645-b2e04c1c82ee?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Volcano" className="rounded-2xl h-64 w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT'S INCLUDED - bento */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <Overline>All-Inclusive</Overline>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-4xl md:text-5xl">What's included</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3">
            {(retreat?.included || []).map((item, i) => (
              <Reveal key={i} delay={(i % 4) * 0.05}>
                <div className="h-full rounded-2xl border border-black/10 bg-white p-6 hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow] duration-200">
                  <Check className="w-5 h-5 text-primary" />
                  <div className="mt-3 font-medium text-sm">{item}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDERS */}
      <section className="bg-[#0D1110] text-white py-24 md:py-32 relative">
        <div className="noise-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <Reveal><Overline>Your Hosts</Overline>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-4xl md:text-5xl">Meet the founders</h2>
            <p className="mt-4 text-white/60 max-w-2xl text-lg">Aida & Edwin Colindres created La Vida Is Travel Fit to combine fitness, travel, adventure, wellness, culture, and community.</p>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 gap-8">
            {founders.map((f, i) => (
              <Reveal key={f.id} delay={i * 0.1}>
                <div className="group rounded-3xl overflow-hidden border border-white/10 bg-white/5">
                  <div className="overflow-hidden h-80">
                    <img src={f.photo} alt={f.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-7">
                    <div className="font-display font-bold text-2xl">{f.name}</div>
                    <div className="text-primary text-sm mt-1">{f.title}</div>
                    <p className="mt-4 text-white/60 text-sm leading-relaxed line-clamp-4">{f.bio}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10"><Link to="/founders"><PillButton variant="light">Read Their Story <ArrowRight className="w-4 h-4" /></PillButton></Link></div>
        </div>
      </section>

      {/* WHY EL SALVADOR */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-8 items-center">
          <Reveal className="md:col-span-5">
            <Overline>The Destination</Overline>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-4xl md:text-5xl leading-[1.05]">{home.why_title || "Why El Salvador"}</h2>
            <p className="mt-6 text-lg text-foreground/70 leading-relaxed">{home.why_body}</p>
            <div className="mt-8"><Link to="/why-el-salvador"><PillButton variant="dark">Discover El Salvador <ArrowRight className="w-4 h-4" /></PillButton></Link></div>
          </Reveal>
          <div className="md:col-span-7 grid grid-cols-2 gap-4">
            <img src="https://images.unsplash.com/photo-1691349810645-b2e04c1c82ee?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Volcano" className="rounded-2xl h-72 w-full object-cover col-span-2" />
            <img src="https://images.unsplash.com/photo-1616028872586-f629c1f29968?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Surf" className="rounded-2xl h-52 w-full object-cover" />
            <img src="https://images.unsplash.com/photo-1519996409144-56c88c9aa612?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Food" className="rounded-2xl h-52 w-full object-cover" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="py-24 bg-secondary/50">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <Reveal><Overline>Loved by athletes</Overline>
              <h2 className="mt-4 font-display font-extrabold tracking-tight text-4xl md:text-5xl">What our travelers say</h2>
            </Reveal>
            <div className="mt-12 grid md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <Reveal key={t.id} delay={i * 0.1}>
                  <div className="h-full rounded-2xl bg-white border border-black/10 p-8">
                    <div className="flex gap-1 text-primary">{Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} className="w-4 h-4 fill-current" />)}</div>
                    <p className="mt-5 text-foreground/80 leading-relaxed">"{t.quote}"</p>
                    <div className="mt-6 font-display font-bold">{t.name}</div>
                    <div className="text-sm text-foreground/50">{t.location}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1740712686694-24eb2ad2a16d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="El Salvador" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center text-white">
          <Reveal>
            <h2 className="font-display font-extrabold tracking-tight text-4xl md:text-6xl leading-[1.02]">Your retreat is waiting.</h2>
            <p className="mt-5 text-lg text-white/80">{retreat && `${fmtDate(retreat.start_date)} · From $${price?.toLocaleString()} · ${retreat.spots_remaining} spots left`}</p>
            <div className="mt-9 flex justify-center gap-4">
              <Link to="/book"><PillButton className="h-14 px-9 text-base">Book Your Retreat <ArrowRight className="w-4 h-4" /></PillButton></Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
